import { z } from "zod";

export type JsonValue =
  | string
  | number
  | boolean
  | null
  | JsonArray
  | JsonObject;

export interface JsonObject {
  [key: string]: JsonValue;
}

export type JsonArray = JsonValue[];

export const jsonValueSchema: z.ZodType<JsonValue> = z
  .lazy(() =>
    z.union([
      z.null(),
      z.string(),
      z.number(),
      z.boolean(),
      z.array(jsonValueSchema),
      z.record(z.string(), jsonValueSchema),
    ]),
  )
  .meta({ ref: "DashboardJsonValue" });

export const jsonObjectSchema: z.ZodType<JsonObject> = z
  .record(z.string(), jsonValueSchema)
  .meta({ ref: "DashboardJsonObject" });

export const DASHBOARD_EVENT_VERSION = 1 as const;

export const dashboardEventTypeValues = [
  "task_callback",
  "task_snapshot",
  "execution_request_snapshot",
  "execution_node_snapshot",
  "compile_recording_snapshot",
  "queue_delta",
  "node_status",
  "plan_revision_updated",
  "provider_progress",
  "repository_chat_snapshot",
  "instance_spawned",
  "instance_status_changed",
  "instance_activity",
  "actor_instance_changed",
  "channel_changed",
  "template_changed",
  "channel_environment_changed",
] as const;

export const dashboardEventTypeSchema = z.enum(dashboardEventTypeValues);

export type DashboardEventType = z.infer<typeof dashboardEventTypeSchema>;

export const dashboardEventHydrationInfoSchema = z
  .object({
    message: z.string().nullable().optional(),
    ok: z.boolean(),
    reason: z.string().nullable().optional(),
  })
  .strict();

export const taskCallbackPayloadSchema = z
  .object({
    callback_dispatched: z.boolean().optional(),
    callback_error: z.string().optional(),
    finalText: z.string().optional(),
    logs: jsonValueSchema.optional(),
    metadata: jsonObjectSchema.optional(),
    node_id: z.string().optional(),
    parsed_result: jsonValueSchema.optional(),
    parsed_result_errors: z.array(jsonValueSchema).optional(),
    parsed_result_source: z.string().optional(),
    progress: z.number().optional(),
    result: jsonValueSchema.optional(),
    runtime: z.number().optional(),
    status: jsonValueSchema.optional(),
    task_id: z.string().min(1),
  })
  .passthrough();

export type TaskCallbackPayload = z.infer<typeof taskCallbackPayloadSchema>;

export const taskSnapshotPayloadSchema = z
  .object({
    organization_id: z.string().nullable().optional(),
    task_id: z.string().min(1),
  })
  .passthrough();

export const executionRequestSnapshotPayloadSchema = z
  .object({ execution_request_id: z.string().min(1) })
  .passthrough();

export const executionNodeSnapshotPayloadSchema = z
  .object({
    execution_request_id: z.string().min(1),
    node_id: z.string().min(1),
  })
  .passthrough();

export const compileRecordingSnapshotPayloadSchema = z
  .object({
    compile_recording_request_id: z.string().min(1),
    execution_request_id: z.string().min(1),
  })
  .passthrough();

export const queueDeltaPayloadSchema = z
  .object({
    delta: z.number().finite(),
    execution_request_id: z.string().min(1).optional(),
    node_id: z.string().min(1).optional(),
    organization_id: z.string().min(1).optional(),
    queue: z.string().min(1),
    task_id: z.string().min(1).optional(),
  })
  .passthrough();

export const nodeStatusPayloadSchema = z
  .object({ node_id: z.string().min(1), status: z.string().min(1).optional() })
  .passthrough();

export const planRevisionUpdatedPayloadSchema = z
  .object({
    flow_status: z.string().min(1).nullable().optional(),
    revision_id: z.string().min(1),
    story_issue_key: z.string().min(1).nullable().optional(),
  })
  .passthrough();

export const providerProgressEventDataSchema = jsonObjectSchema;

export const providerProgressPayloadSchema = z
  .object({
    event_data: providerProgressEventDataSchema,
    event_timestamp_ms: z.number().finite().int().nullable().optional(),
    event_type: z.string().trim().min(1),
    execution_request_id: z.string().trim().min(1).nullable().optional(),
    node_id: z.string().trim().min(1).nullable().optional(),
    organization_id: z.string().trim().min(1).nullable().optional(),
    run_id: z.string().trim().min(1).nullable().optional(),
    stream_id: z.string().trim().min(1).nullable().optional(),
    task_id: z.string().trim().min(1),
    ticket_id: z.string().trim().min(1).nullable().optional(),
  })
  .passthrough();

export type ProviderProgressPayload = z.infer<
  typeof providerProgressPayloadSchema
>;

export const repositoryChatSnapshotPayloadSchema = z
  .object({
    assistant_message_id: z.string().min(1).nullable().optional(),
    conversation_id: z.string().min(1),
    organization_id: z.string().min(1).nullable().optional(),
    reason: z.enum(["assistant_message_persisted", "turn_updated"]),
    status: z.string().min(1).nullable().optional(),
    task_id: z.string().min(1).nullable().optional(),
    turn_id: z.string().min(1).nullable().optional(),
  })
  .passthrough();

export type RepositoryChatSnapshotPayload = z.infer<
  typeof repositoryChatSnapshotPayloadSchema
>;

function createDashboardEventEnvelopeSchema<
  TType extends DashboardEventType,
  TPayloadSchema extends z.ZodType,
>(type: TType, payload: TPayloadSchema) {
  return z
    .object({
      correlation_id: z.string().nullable().optional(),
      hydration: dashboardEventHydrationInfoSchema.optional(),
      payload,
      source: z.string().min(1),
      timestamp: z.number().finite(),
      type: z.literal(type),
      version: z.literal(DASHBOARD_EVENT_VERSION),
    })
    .strict();
}

export const taskCallbackEventSchema = createDashboardEventEnvelopeSchema(
  "task_callback",
  taskCallbackPayloadSchema,
);

export type TaskCallbackEvent = z.infer<typeof taskCallbackEventSchema>;

export const taskSnapshotEventSchema = createDashboardEventEnvelopeSchema(
  "task_snapshot",
  taskSnapshotPayloadSchema,
);

export type TaskSnapshotEvent = z.infer<typeof taskSnapshotEventSchema>;

export const executionRequestSnapshotEventSchema =
  createDashboardEventEnvelopeSchema(
    "execution_request_snapshot",
    executionRequestSnapshotPayloadSchema,
  );

export type ExecutionRequestSnapshotEvent = z.infer<
  typeof executionRequestSnapshotEventSchema
>;

export const executionNodeSnapshotEventSchema =
  createDashboardEventEnvelopeSchema(
    "execution_node_snapshot",
    executionNodeSnapshotPayloadSchema,
  );

export type ExecutionNodeSnapshotEvent = z.infer<
  typeof executionNodeSnapshotEventSchema
>;

export const compileRecordingSnapshotEventSchema =
  createDashboardEventEnvelopeSchema(
    "compile_recording_snapshot",
    compileRecordingSnapshotPayloadSchema,
  );

export const queueDeltaEventSchema = createDashboardEventEnvelopeSchema(
  "queue_delta",
  queueDeltaPayloadSchema,
);

export type QueueDeltaEvent = z.infer<typeof queueDeltaEventSchema>;

export const nodeStatusEventSchema = createDashboardEventEnvelopeSchema(
  "node_status",
  nodeStatusPayloadSchema,
);

export type NodeStatusEvent = z.infer<typeof nodeStatusEventSchema>;

export const planRevisionUpdatedEventSchema =
  createDashboardEventEnvelopeSchema(
    "plan_revision_updated",
    planRevisionUpdatedPayloadSchema,
  );

export type PlanRevisionUpdatedEvent = z.infer<
  typeof planRevisionUpdatedEventSchema
>;

export const providerProgressEventSchema = createDashboardEventEnvelopeSchema(
  "provider_progress",
  providerProgressPayloadSchema,
);

export type ProviderProgressEvent = z.infer<typeof providerProgressEventSchema>;

export const repositoryChatSnapshotEventSchema =
  createDashboardEventEnvelopeSchema(
    "repository_chat_snapshot",
    repositoryChatSnapshotPayloadSchema,
  );

export const instanceSpawnedPayloadSchema = z.object({
  avatar_seed: z.string(),
  channels: z.array(z.object({ display_name: z.string(), id: z.string() })),
  display_name: z.string(),
  instance_id: z.string(),
  organization_id: z.string(),
  status: z.string(),
});

export const instanceSpawnedEventSchema = createDashboardEventEnvelopeSchema(
  "instance_spawned",
  instanceSpawnedPayloadSchema,
);

export const instanceStatusChangedPayloadSchema = z.object({
  current_run_id: z.string().nullable().optional(),
  instance_id: z.string(),
  organization_id: z.string(),
  previous_status: z.string(),
  status: z.string(),
  wake_blocked_at: z.string().datetime().nullable().optional(),
  wake_blocked_consecutive_failure_count: z
    .number()
    .int()
    .nonnegative()
    .nullable()
    .optional(),
  wake_blocked_error: z.string().nullable().optional(),
  wake_blocked_reason: z.string().nullable().optional(),
  wake_blocked_trigger_channel_message_id: z.string().nullable().optional(),
  wake_blocked_until: z.string().datetime().nullable().optional(),
});

export const instanceStatusChangedEventSchema =
  createDashboardEventEnvelopeSchema(
    "instance_status_changed",
    instanceStatusChangedPayloadSchema,
  );

// ---------------------------------------------------------------------------
// Instance activity — discriminated union on activity_type
//
// Each activity type carries its own typed `detail` shape, derived from the
// producer emit sites (verified across orchestrator + node). Three types
// (tool_call, phase_transition, budget_warning) were removed: they were never
// emitted as instance_activity — tool_call collides with the separate
// chat:message event system, and the other two had no emit site in any commit
// in the repo's history.
//
// ---------------------------------------------------------------------------

export const instanceActivityTypeSchema = z.enum([
  "channel_message",
  "channel_wake_claimed",
  "cache_stats",
  "token_delta",
  "status_change",
  "evolution_cycle",
  "context_window",
  "actor_park",
  "actor_wake",
  "consolidation_only_wake",
  "external_compact_seed",
  "cache_break_detected",
  "channel_lock_waiting",
  "channel_lock_acquired",
  "step_completed",
]);

export const instanceActivityWakeStateSchema = z.enum([
  "preparing",
  "lock_waiting",
  "llm_call",
  "between_steps",
]);

// Shared fields across all activity variants (not part of detail).
const instanceActivitySharedFields = {
  current_run_id: z.string().nullable().optional(),
  instance_id: z.string(),
  organization_id: z.string(),
  wake_state: instanceActivityWakeStateSchema.optional(),
};

// --- Per-type detail schemas (producer-verified canonical shapes) ---

export const channelMessageActivityDetailSchema = z.object({
  channel_id: z.string(),
  content: z.string(),
  message_id: z.string().nullable(),
  recipient_count: z.number().int(),
  running_instance_ids: z.array(z.string()).optional(),
  sender_display_name: z.string(),
  sender_id: z.string(),
  sender_type: z.enum(["instance", "user", "system"]),
  woken_instance_ids: z.array(z.string()).optional(),
});

export const cacheStatsActivityDetailSchema = z.object({
  break_reason: z.string().optional(),
  break_reason_detail: z.string().nullable().optional(),
  cache_hit_pct: z.number().nullable(),
  cache_read_tokens: z.number(),
  cache_ttl_ms: z.number().optional(),
  /** Channel the actor is working on. Present when the node has channel
   *  context (chat mode); absent for actor-level activations without a channel. */
  channel_id: z.string().optional(),
  first_step_cache_read_tokens: z.number().optional(),
  first_step_hit_pct: z.number().optional(),
  is_break: z.boolean(),
  reason: z.string().optional(),
  slept_ms_since_prior_park: z.number().optional(),
  system_prompt_prefix_sha: z.string().optional(),
});

export const tokenDeltaActivityDetailSchema = z.object({
  cache_read_tokens_delta: z.number(),
  cache_write_tokens_delta: z.number(),
  /** Channel the actor is working on. See cacheStatsActivityDetailSchema. */
  channel_id: z.string().optional(),
  cost_delta_usd: z.number(),
  input_tokens_delta: z.number(),
  no_cache_tokens_delta: z.number(),
  output_tokens_delta: z.number(),
});

export const statusChangeActivityDetailSchema = z.object({
  channel_id: z.string().optional(),
  error_summary: z.string().nullable().optional(),
  reason: z.string().optional(),
  status: z.string(),
  wake_blocked_at: z.string().nullable().optional(),
  wake_blocked_consecutive_failure_count: z
    .number()
    .int()
    .nullable()
    .optional(),
  wake_blocked_error: z.string().nullable().optional(),
  wake_blocked_reason: z.string().nullable().optional(),
  wake_blocked_until: z.string().nullable().optional(),
});

export const evolutionCycleActivityDetailSchema = z.object({
  evolution: z.number(),
});

export const contextWindowActivityDetailSchema = z.object({
  channel_id: z.string().optional(),
  context_growth_since_evolution: z.number(),
  context_growth_this_wake: z.number(),
  context_window_size: z.number(),
  current_phase: z.string(),
  phase_budget_remaining: z.number(),
});

export const actorParkActivityDetailSchema = z.object({
  channel_id: z.string().optional(),
  error_summary: z.string().nullable().optional(),
  parked_at: z.string(),
  trigger: z.string(),
});

const wakeActivityDetailSharedFields = {
  current_step_id: z.string().optional(),
  wake_group_id: z.string().optional(),
};

const wakeStartedActivityDetailSchema = z.object({
  ...wakeActivityDetailSharedFields,
  trigger: z.string(),
  wake_state: instanceActivityWakeStateSchema.optional(),
  woke_at: z.string().datetime(),
});

const wakeStateActivityDetailSchema = z.object({
  ...wakeActivityDetailSharedFields,
  trigger: z.string().optional(),
  wake_state: instanceActivityWakeStateSchema,
  woke_at: z.never().optional(),
});

export const actorWakeActivityDetailSchema = z.union([
  wakeStartedActivityDetailSchema.extend({ channel_id: z.string().optional() }),
  wakeStateActivityDetailSchema.extend({ channel_id: z.string().optional() }),
]);

export const consolidationOnlyWakeActivityDetailSchema = z.union([
  wakeStartedActivityDetailSchema,
  wakeStateActivityDetailSchema,
]);

export const externalCompactSeedActivityDetailSchema = z.union([
  wakeStartedActivityDetailSchema,
  wakeStateActivityDetailSchema,
]);

export const cacheBreakDetectedActivityDetailSchema = z.object({
  activation_index: z.number(),
  channel_id: z.string().optional(),
  current_pct: z.number(),
  delta_pp: z.number(),
  prev_rolling_avg_pct: z.number(),
});

export const channelLockWaitingActivityDetailSchema = z.object({
  /** `channel_id` and `holder_id` are present when emitted by the lock-contender
   *  path (inFlightLocks.ts). The wake-state emit path (chatModeHandler)
   *  emits channel_lock_waiting without them — it only carries wake_group_id. */
  channel_id: z.string().optional(),
  current_step_id: z.string().optional(),
  holder_id: z.string().optional(),
  wake_group_id: z.string().optional(),
  wake_state: z.string().optional(),
});

export const channelLockAcquiredActivityDetailSchema = z.object({
  /** Raw channel id. Present for shared-channel locks; omitted for instance-
   *  scoped (self) locks, which have no channel to attribute. */
  channel_id: z.string().optional(),
  holder_id: z.string(),
  waited_ms: z.number(),
  was_contended: z.boolean(),
});

export const stepCompletedActivityDetailSchema = z.object({
  cache_read_tokens: z.number().optional(),
  cache_write_tokens: z.number().optional(),
  /** Channel the actor is working on. See cacheStatsActivityDetailSchema. */
  channel_id: z.string().optional(),
  duration_ms: z.number().nullable(),
  error_kind: z.string().nullable(),
  finish_reason: z.string().nullable(),
  input_tokens: z.number().optional(),
  lock_wait_ms: z.number().nullable(),
  output_tokens: z.number().optional(),
  phase: z.string().nullable(),
  started_at: z.string(),
  step_id: z.string(),
  step_index_in_wake: z.number(),
  step_kind: z.string(),
  trigger_reason: z.string().nullable(),
  wake_group_id: z.string(),
});
export type CacheStatsActivityDetail = z.infer<
  typeof cacheStatsActivityDetailSchema
>;

export const instanceActivityPayloadSchema = z.discriminatedUnion(
  "activity_type",
  [
    z.object({
      activity_type: z.literal("channel_message"),
      detail: channelMessageActivityDetailSchema,
      ...instanceActivitySharedFields,
    }),
    z.object({
      activity_type: z.literal("channel_wake_claimed"),
      detail: z.object({
        channel_id: z.string(),
        claimed_at: z.string().datetime(),
      }),
      ...instanceActivitySharedFields,
    }),
    z.object({
      activity_type: z.literal("cache_stats"),
      detail: cacheStatsActivityDetailSchema,
      ...instanceActivitySharedFields,
    }),
    z.object({
      activity_type: z.literal("token_delta"),
      detail: tokenDeltaActivityDetailSchema,
      ...instanceActivitySharedFields,
    }),
    z.object({
      activity_type: z.literal("status_change"),
      detail: statusChangeActivityDetailSchema,
      ...instanceActivitySharedFields,
    }),
    z.object({
      activity_type: z.literal("evolution_cycle"),
      detail: evolutionCycleActivityDetailSchema,
      ...instanceActivitySharedFields,
    }),
    z.object({
      activity_type: z.literal("context_window"),
      detail: contextWindowActivityDetailSchema,
      ...instanceActivitySharedFields,
    }),
    z.object({
      activity_type: z.literal("actor_park"),
      detail: actorParkActivityDetailSchema,
      ...instanceActivitySharedFields,
    }),
    z.object({
      activity_type: z.literal("actor_wake"),
      detail: actorWakeActivityDetailSchema,
      ...instanceActivitySharedFields,
    }),
    z.object({
      activity_type: z.literal("consolidation_only_wake"),
      detail: consolidationOnlyWakeActivityDetailSchema,
      ...instanceActivitySharedFields,
    }),
    z.object({
      activity_type: z.literal("external_compact_seed"),
      detail: externalCompactSeedActivityDetailSchema,
      ...instanceActivitySharedFields,
    }),
    z.object({
      activity_type: z.literal("cache_break_detected"),
      detail: cacheBreakDetectedActivityDetailSchema,
      ...instanceActivitySharedFields,
    }),
    z.object({
      activity_type: z.literal("channel_lock_waiting"),
      detail: channelLockWaitingActivityDetailSchema,
      ...instanceActivitySharedFields,
    }),
    z.object({
      activity_type: z.literal("channel_lock_acquired"),
      detail: channelLockAcquiredActivityDetailSchema,
      ...instanceActivitySharedFields,
    }),
    z.object({
      activity_type: z.literal("step_completed"),
      detail: stepCompletedActivityDetailSchema,
      ...instanceActivitySharedFields,
    }),
  ],
);

export type InstanceActivityPayload = z.infer<
  typeof instanceActivityPayloadSchema
>;

export const instanceActivityEventSchema = createDashboardEventEnvelopeSchema(
  "instance_activity",
  instanceActivityPayloadSchema,
);

export const actorInstanceChangedPayloadSchema = z.object({
  change_type: z.enum(["create", "update", "delete", "lifecycle"]),
  instance_id: z.string(),
  organization_id: z.string(),
  status: z.string().nullable(),
});

export const actorInstanceChangedEventSchema =
  createDashboardEventEnvelopeSchema(
    "actor_instance_changed",
    actorInstanceChangedPayloadSchema,
  );

export const channelChangedPayloadSchema = z.object({
  active_teams_binding_id: z.string().nullable(),
  change_type: z.enum([
    "create",
    "update",
    "delete",
    "binding",
    "membership",
    "archive",
    "environment",
  ]),
  channel_id: z.string(),
  organization_id: z.string(),
});

export const channelChangedEventSchema = createDashboardEventEnvelopeSchema(
  "channel_changed",
  channelChangedPayloadSchema,
);

export type ChannelChangedEvent = z.infer<typeof channelChangedEventSchema>;

export const templateChangedPayloadSchema = z.object({
  change_type: z.enum(["create", "update", "delete"]),
  organization_id: z.string(),
  template_id: z.string(),
});

export const templateChangedEventSchema = createDashboardEventEnvelopeSchema(
  "template_changed",
  templateChangedPayloadSchema,
);

export const channelEnvironmentChangedPayloadSchema = z.object({
  change_type: z.enum([
    "create",
    "update",
    "delete",
    "restart",
    "archive",
    "retain",
    "recover",
    "repository",
  ]),
  channel_environment_id: z.string().nullable(),
  channel_id: z.string(),
  organization_id: z.string(),
});

export const channelEnvironmentChangedEventSchema =
  createDashboardEventEnvelopeSchema(
    "channel_environment_changed",
    channelEnvironmentChangedPayloadSchema,
  );

export const dashboardEventSchema = z.discriminatedUnion("type", [
  taskCallbackEventSchema,
  taskSnapshotEventSchema,
  executionRequestSnapshotEventSchema,
  executionNodeSnapshotEventSchema,
  compileRecordingSnapshotEventSchema,
  queueDeltaEventSchema,
  nodeStatusEventSchema,
  planRevisionUpdatedEventSchema,
  providerProgressEventSchema,
  repositoryChatSnapshotEventSchema,
  instanceSpawnedEventSchema,
  instanceStatusChangedEventSchema,
  instanceActivityEventSchema,
  actorInstanceChangedEventSchema,
  channelChangedEventSchema,
  templateChangedEventSchema,
  channelEnvironmentChangedEventSchema,
]);

export type DashboardEvent = z.infer<typeof dashboardEventSchema>;

export function parseDashboardEvent(value: unknown): DashboardEvent | null {
  const parsed = dashboardEventSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

export function isDashboardEvent(value: unknown): value is DashboardEvent {
  return dashboardEventSchema.safeParse(value).success;
}

export function isTaskCallbackPayload(
  value: unknown,
): value is TaskCallbackPayload {
  return taskCallbackPayloadSchema.safeParse(value).success;
}
